import { actions as notificationActions } from './notification';

import ActionHelper from '@module/redux-actions';

import {
  MAX_MESSAGE_SIZE_IN_BYTES,
  REGEXP_EMAIL,
  REGEXP_BLANK,
} from '@app/transfer-bundle/config/transfer-config';
import { extractEmails } from '@app/transfer-bundle/helpers/extract-emails';

export const actions = ActionHelper.types([
  'TRANSFER_PREPARE_DOWNLOAD',
  // this should be replace with a action that will create a transfer
  // in the reducer and replace the old Transfer class
  // so transfer lives 100% in the state
  'TRANSFER_INIT',
  'TRANSFER_CREATE',
  'TRANSFER_CREATE_FORWARD',
  'TRANSFER_CREATE_SIMILAR',
  'TRANSFER_CHANGE_TYPE',
  'TRANSFER_CHANGE_EXPIRY',
  'TRANSFER_CHANGE_PASSWORD',
  'TRANSFER_SET_SENDER',
  'TRANSFER_SET_MESSAGE',
  'TRANSFER_SET_LANGUAGE',
  'TRANSFER_SET_DOMAIN_USER_ID',
  'TRANSFER_SET_CHANNEL',
  'TRANSFER_ADD_RECIPIENT',
  'TRANSFER_ADD_FILES',
  'TRANSFER_REMOVE_FILE',
  'TRANSFER_REMOVE_DIRECTORY',
  'TRANSFER_REMOVE_RECIPIENT',
  'TRANSFER_RESET',
  'TRANSFER_START',
  'TRANSFER_STATE_DOWNLOADABLE',
  'TRANSFER_STATE_DOWNLOAD_STARTING',
  'TRANSFER_STATE_DOWNLOADING',
  'TRANSFER_STATE_FINISHED',
  'TRANSFER_RESET_DOWNLOAD',
  'TRANSFER_SET_STATE',
  'TRANSFER_SET_EXPIRY_SECONDS',
  'TRANSFER_SET_SHORT_URL',
  'TRANSFER_SET_ID',
  'TRANSFER_SET_DISPLAY_NAME',
  'TRANSFER_SET_SECURITY_HASH',
  'TRANSFER_SET_UPLOAD_STARTED_AT',

  // Download related
  'DOWNLOAD_PENDING',
  'DOWNLOAD_SUCCESS',
  'DOWNLOAD_ERROR',
  'TRANSFER_DOWNLOAD_EXPIRED',
  'TRANSFER_DOWNLOAD_INVALID',
  'TRANSFER_STATE_DOWNLOAD_STARTING_FILE',
  'TRANSFER_STATE_DOWNLOADING_FILE',
  'TRANSFER_STATE_FINISHED_FILE',

  'RECAPTCHA_CHALLENGE_RECEIVED',

  // Upload related
  'UPLOAD_PENDING',
  'UPLOAD_RESET',
  'UPLOAD_ERROR',
  'UPLOAD_ERROR_RESET',
  'UPLOAD_NEED_VERIFICATION',
  'UPLOAD_VERIFIED',
  'UPLOAD_DONE',

  // Preview related
  'PREVIEW_LOADING',
  'PREVIEW_LOADED',
  'PREVIEW_ERROR',
]);

export function dispatchNotification(dispatch, transfer, channel, text) {
  const ignoredErrorMessages = [
    'LimitReached',
    'UserBlocked',
    'TransferFailed',
    'UnavailableParent',
    'TransferCanceled',
    'HumanVerificationRequired',
  ];
  if (ignoredErrorMessages.indexOf(text) !== -1) {
    return;
  }

  // NOTE: This is for the 20gb channel feature
  if (text === 'TransferTooBig') {
    const containsRecipient =
      transfer.recipients.length === 1 &&
      transfer.recipients[0] === channel.defaultRecipient;
    if (channel.defaultRecipient && !containsRecipient) {
      text = 'RequiresDefaultRecipient';
    }
  }

  // Dispatch the notification
  dispatch({ type: notificationActions.NOTIFICATION_TRANSFER_SET, text });
}

export default ActionHelper.generate({
  createSimilar: [
    actions.TRANSFER_CREATE_SIMILAR,
    'transfer',
    'user',
    'account',
    'channel',
  ],
  changeType: [actions.TRANSFER_CHANGE_TYPE, 'transferType'],
  changeExpiry: [actions.TRANSFER_CHANGE_EXPIRY, 'period'],
  changePassword: [actions.TRANSFER_CHANGE_PASSWORD, 'password'],
  removeFile: [actions.TRANSFER_REMOVE_FILE, 'fileName'],
  removeDirectory: [actions.TRANSFER_REMOVE_DIRECTORY, 'directoryName'],
  removeRecipient: [actions.TRANSFER_REMOVE_RECIPIENT, 'email'],
  clearUploadError: [actions.UPLOAD_ERROR_RESET],
  displayError: [actions.UPLOAD_ERROR, 'error', 'field'],
  resetDownload: [actions.TRANSFER_RESET_DOWNLOAD],
  setDownloadable: [actions.TRANSFER_STATE_DOWNLOADABLE, 'transferId'],
  previewLoading: [actions.PREVIEW_LOADING, 'file_id', 'file_url'],
  previewLoaded: [actions.PREVIEW_LOADED, 'file_id'],
  previewError: [actions.PREVIEW_ERROR, 'file_id'],
  recaptcha: [actions.RECAPTCHA_CHALLENGE_RECEIVED, 'transfer_id'],

  init({ transferType, userType, channelName, accountId, spaceId }) {
    return {
      type: actions.TRANSFER_INIT,
      transferType: transferType || 'email',
      userType: userType || 'free',
      ...(channelName ? { channelName } : {}),
      ...(accountId ? { accountId } : {}),
      ...(spaceId ? { spaceId } : {}),
    };
  },

  create() {
    return (dispatch, getState) => {
      const { user, channel, account, transfer } = getState();
      dispatch({
        type: actions.TRANSFER_CREATE,
        user,
        channel,
        account,
        transfer,
      });
    };
  },

  // Verify the download
  verifiedPassword(password) {
    return (dispatch) => {
      dispatch({ type: actions.TRANSFER_CHANGE_PASSWORD, password });
    };
  },

  // Validate and set the Transfer sender
  setSender(email) {
    return (dispatch) => {
      if (!email.replace(REGEXP_BLANK, '')) {
        dispatch({ type: actions.TRANSFER_SET_SENDER, email: '' });
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'MissingSender',
        });
      } else if (!email.match(REGEXP_EMAIL)) {
        dispatch({ type: actions.TRANSFER_SET_SENDER, email: '' });
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'InvalidSender',
        });
      }

      return dispatch({ type: actions.TRANSFER_SET_SENDER, email });
    };
  },

  // Validate and set the Transfer message
  setMessage(message) {
    const byteSize = unescape(encodeURIComponent(message)).length;

    if (byteSize > MAX_MESSAGE_SIZE_IN_BYTES) {
      return {
        type: notificationActions.NOTIFICATION_TRANSFER_SET,
        text: 'MessageTooLarge',
      };
    }

    // Check max length (bytes)
    return { type: actions.TRANSFER_SET_MESSAGE, message };
  },

  // Validate and add a Transfer recipient
  addRecipient(email) {
    return (dispatch, getState) => {
      const { transfer, user } = getState();
      const emails = extractEmails(email);

      // We only allow so many recipients for a transfer
      if (
        transfer.recipients.length + emails.length >
        user.rules.maximumNumberOfRecipients
      ) {
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'TooManyRecipients',
        });
      }

      // Nothing filled in
      if (!email.replace(REGEXP_BLANK, '') && emails.length === 0) {
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'MissingRecipients',
        });
      }

      // Doesn't match.... the regexp!
      if (!email.match(REGEXP_EMAIL) && emails.length <= 1) {
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'InvalidRecipient',
        });
      }

      return dispatch({ type: actions.TRANSFER_ADD_RECIPIENT, emails });
    };
  },
});
